import { uuidv7 } from "uuidv7"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { groupFor, hasGroup, PropertyGroup, PropertyId, PropertySchema } from "~/classes/entity"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { StaticFile } from "~/classes/file/StaticFile"
import { sendFolderContentHistoryRequests } from "~/classes/handlers/file/content-history"
import { sendFileHistoryRequests } from "~/classes/handlers/file/history"
import { sendFileRevisionRequests } from "~/classes/handlers/file/revision"

export interface CreationHistoryEvent {
  type: "create"
  author: string
  timestamp: string
  name: string
  activity: PropertyHistorySegment
}
export interface MoveHistoryEvent {
  type: "move"
  author: string
  timestamp: string
  parentId: string
  activity: PropertyHistorySegment
}
export interface EditHistoryEvent {
  type: "edit"
  author: string
  timestamp: string
  activity: PropertyHistorySegment
}
export interface UpdateHistoryEvent {
  type: "update"
  author: string
  timestamp: string
  activity: PropertyHistorySegment
}
export interface SystemHistoryEvent {
  type: "system"
  author: string
  timestamp: string
  name: string
  activity: PropertyHistorySegment
}
export type HistoryEvent = CreationHistoryEvent | MoveHistoryEvent | SystemHistoryEvent | UpdateHistoryEvent | EditHistoryEvent

/** A single history item, i.e. a cluster of events tied to a timestamp */
export type PropertyHistorySegment = Partial<Record<PropertyId, FileProperty>>
/**  History items in a dictionary by timestamp, then by property id. */
export type PropertyHistoryDict = Record<string, PropertyHistorySegment>

export type MultiFileHistoryDict = Record<
  string, // timestamp
  Record<
    string, // file id
    PropertyHistorySegment
  >
>

class FileActivity extends GlobalComposable<{
  targetFileId?: string
  fileHistory: PropertyHistoryDict
  folderHistory: MultiFileHistoryDict
}> {
  override defaultState = () => ({
    fileHistory: { },
    folderHistory: { },
  })

  /**
   * Currently, you can only look at one file's history at a time. So we take the selection
   * if it is just one item.
   */
  targetFile = computed(() => {
    const { targetFiles } = usePanel()
    if (len(targetFiles.value) !== 1)
      return
    return first(targetFiles.value)
  })

  /**
   * When you open the activity panel this callback needs to get called with a
   * watcher over targetFile
   */
  onTargetUpdated = (newFile?: StaticFile, oldFile?: StaticFile) => {
    const { addEntityEventListener, removeEntityEventListener } = useEvents()
    const { isOffline } = useCache()

    if (newFile && newFile?.id === oldFile?.id)
      return this.warn("File activity: fileId is the same, not updating")
    this.state.value.targetFileId = newFile?.id

    if (oldFile) {
      removeEntityEventListener("fileProperties", oldFile.id, this.onFileProperties)
      removeEntityEventListener("fileHistory", oldFile.id, this.onFileHistory)
      removeEntityEventListener("folderHistory", oldFile.id, this.onFolderHistory)
      this.state.value.fileHistory = {}
    }

    if (isDefined(newFile)) {
      const historyRequests: FileHistoryRequestDatagram[] = []
      const folderHistoryRequests: FolderContentHistoryRequestDatagram[] = []
      addEntityEventListener("fileProperties", newFile.id, this.onFileProperties)
      addEntityEventListener("fileHistory", newFile.id, this.onFileHistory)
      const mimeProperty = newFile.properties[PropertyId.FILE_MIME_TYPE]!
      const file = StaticFile.blank(newFile.id)
      file.addProperty(mimeProperty)

      historyRequests.push({
        requestId: generateWorkerRequestId(),
        file: file.inner(),
        offline: isOffline(),
        pageSize: 100,
        limit: 100,
      })

      if (newFile.isFolder()) {
        addEntityEventListener("folderHistory", newFile.id, this.onFolderHistory)
        folderHistoryRequests.push({
          requestId: generateWorkerRequestId(),
          folder: file.inner(),
          offline: isOffline(),
          pageSize: 100,
          limit: 100,
        })
      }

      if (historyRequests.length)
        sendFileHistoryRequests(historyRequests)
      if (folderHistoryRequests.length)
        sendFolderContentHistoryRequests(folderHistoryRequests)
    }
  }

  onFileProperties = (file: FileEvent) => {
    if (this.state.value.targetFileId !== file.id)
      return this.warn("Received file properties for a file that is not the target", file)
    // console.log("File activity: file properties", file)
    return this.onFileHistory({
      fileId: file.id,
      properties: Object.values(file.properties) as FileProperty[],
    })
  }

  onFileHistory = ({ fileId, properties }: FileHistoryEvent) => {
    if (this.state.value.targetFileId !== fileId)
      return this.warn("Received history properties for a file that is not the target", properties)
    // this.debug("file history received", properties)
    for (const property of properties) {
      if (!property)
        continue
      if (!this.state.value.fileHistory[property.timestamp])
        this.state.value.fileHistory[property.timestamp] = {}
      this.state.value.fileHistory[property.timestamp][property.propertyId as PropertyId] = property
    }
  }

  onFolderHistory = ({ properties }: FolderHistoryEvent) => {
    this.debug("folder history received", properties)
    // TODO: only check history for one item at a time
    // TODO: retrieve all file properties for items in this folder
    // TODO: listen for folder list requests as a way to not have to do lots of properties requests
  }

  canBeUpdated = (property: FileProperty) => {
    if ([
      PropertyId.FILE_NAME,
      PropertyId.FILE_RATING,
      PropertyId.FILE_NOTES,
    ].includes(property.propertyId as PropertyId))
      return true
    if (hasGroup(property.propertyId as PropertyId, PropertyGroup.FileTagMetaProperty))
      return true
    return false
  }

  parseActivityAsEvent = (activity: PropertyHistorySegment): HistoryEvent | undefined => {
    if (activity[PropertyId.FILE_CREATED_AT] && activity[PropertyId.FILE_NAME]) {
      const fileName = activity[PropertyId.FILE_NAME]!
      const author = fileName.author
      const timestamp = fileName.timestamp
      if (fileName.value.type !== PropertySchema.NonEmptyStringValue)
        throw new Error("File name is not a non-empty string")
      return {
        type: "create",
        author,
        timestamp,
        name: fileName.value.nonEmptyString,
        activity,
      }
    }

    // If any of the properties can be considered an "update" property, this entire activity cluster is an update.
    const updatableProperty = Object
      .values(activity)
      .find(this.canBeUpdated)
    if (isDefined(updatableProperty)) {
      const author = updatableProperty.author
      const timestamp = updatableProperty.timestamp
      return {
        type: "update",
        author,
        timestamp,
        activity,
      }
    }

    if (activity[PropertyId.FILE_PARENT_ID]) {
      const fileParent = activity[PropertyId.FILE_PARENT_ID]!
      const author = fileParent.author
      const timestamp = fileParent.timestamp

      // We only want the _versioned_ status, not the unofficial status, as
      // the unofficial status represents the old parent id.
      if (
        fileParent.version
        && fileParent.version > 0
        && fileParent.value.type === PropertySchema.FileIdValue
      ) return {
        type: "move",
        author,
        timestamp,
        parentId: fileParent.value.fileId,
        activity,
      }
      else {
        return
      }
    }

    // ignore search indexing statuses for now
    if (activity[PropertyId.FILE_SEARCH_INDEXING_STATUS]) {
      return
      // const status = activity[Property.FILE_SEARCH_INDEXING_STATUS]!
      // const timestamp = status.timestamp
      // return {
      //   type: "indexing",
      //   timestamp,
      // }
    }

    if (activity[PropertyId.FOLDER_USAGE_QUANTITY] || activity[PropertyId.FOLDER_USAGE_SIZE] || activity[PropertyId.FOLDER_USAGE_UPDATED_AT])
      return

    // Don't worry about client properties
    if (activity[PropertyId.FILE_CLIENT_PROPERTY_SELECTION] || activity[PropertyId.FOLDER_CLIENT_REQUEST])
      return

    // Don't worry about accessed at
    if (activity[PropertyId.FILE_ACCESSED_AT])
      return

    // Don't worry about file ref ids, but do this last as it might be in another group like create
    if (any(Object.values(activity), p => groupFor(p.propertyId as PropertyId) === PropertyGroup.FileRefIdMetaProperty))
      return

    this.debug("Unknown history event type", activity)
  }

  /** Sorted by most recent first */
  fileHistoryEvents = computed(() => {
    const { state: panelState } = usePanel()
    let allEvents = sortedByKey(this.state.value.fileHistory)
      .reverse()
      .map(this.parseActivityAsEvent)
      .filter(x => isDefined(x))
    if (!panelState.value.showHistoryMetadata)
      allEvents = allEvents.filter(x => !["update", "create"].includes(x.type))
    if (!panelState.value.showHistoryEdits)
      allEvents = allEvents.filter(x => x.type !== "edit")
    return allEvents
  })

  /**
   * Lots of todos on this one:
   * - [ ] Do this in a scalable way across multiple properties
   * - [ ] Ensure that this translates into just one undo/redo
   * - [ ] Make this work with edit requests
   * - [ ] Make this work with move requests
   * - [ ] Migrate this entire implementation to Rust
   */
  revertTo = async (event: HistoryEvent) => {
    const { waitForServerResponse } = useEvents()
    const { notifyError } = useAlerts()

    if (!this.state.value.targetFileId || !this.targetFile.value)
      return this.error("No target file id set, cannot revert to event", event)

    const targetFile = this.targetFile.value

    const laterEvents = this.fileHistoryEvents.value.filter(e => e.timestamp > event.timestamp)

    console.debug("Later events", laterEvents)

    const changedProperties: Record<string, FileProperty> = {}

    for (const laterEvent of laterEvents) {
      for (const [propertyId, property] of Object.entries(laterEvent.activity)) {
        if (!this.canBeUpdated(property))
          continue

        const currentProperty = targetFile.property(propertyId as PropertyId)

        if (currentProperty && currentProperty.value === property.value && currentProperty.deleted === property.deleted)
          continue
        if (!currentProperty && property.deleted)
          continue

        changedProperties[propertyId] = property
      }
    }

    const properties = Object.keys(changedProperties) as PropertyId[]

    const requestId = generateWorkerRequestId()
    sendFileRevisionRequests([{
      requestId,
      fileId: this.state.value.targetFileId,
      offline: false,
      selection: InterfaceSelection.minimumInterfaceSelection().inner(),
      properties,
      timestamp: event.timestamp,
    }])

    const response = await waitForServerResponse("fileRevisionResponse", requestId)
    if (response.error || !response.file)
      return notifyError("Error reverting file", response.error?.details ?? "Unknown error")

    const timestamp = uuidv7()

    const updates = []

    // added properties
    for (const property of Object.values(response.file.properties).filter(p => isDefined(p)))
      updates.push({
        propertyId: property.propertyId as PropertyId,
        newVal: property.value,
        deleted: property.deleted,
        unversioned: false,
        timestamp,
      })

    // deleted properties
    for (const propertyId of properties
      .filter(p => !isDefined(response.file!.properties[p]))
      .filter(p => isDefined(targetFile.property(p))))
      updates.push({
        propertyId,
        newVal: targetFile.property(propertyId as PropertyId)!.value,
        deleted: true,
        unversioned: false,
        timestamp,
      })

    targetFile.updateProperties(updates)
  }
}

const fileActivity = new FileActivity("file-activity", theme.colors.green.hex)
export default function () {
  return fileActivity.use()
}
